import { z } from 'zod';

import { ANNO_COLOR_KEYS } from '../constants/inspection-annotations';

// ============================================================================
// Inspection Report document (structured JSON stored in inspection_reports.document)
//
// The document is a list of pages, each built from a per-page layout template.
// Photos and their annotations are stored in normalized 0..1 space relative to
// the original photo, so they render identically at any pixel size (editor
// thumbnail vs. full-size PDF). Page-level annotations (see inspectionPageSchema)
// are normalized 0..1 relative to the whole page box instead. See the design
// spec for the parity strategy.
// ============================================================================

// v1 layout ids. Adding a layout is a registry + CSS change (see layout registry
// issue); the document schema only needs to know the set of valid ids.
export const INSPECTION_LAYOUT_IDS = [
  'hero',
  'two-stack',
  'two-text',
  'three',
  'two-row-text',
  'three-row-text',
  'four-row-text',
  'text',
] as const;

export const inspectionLayoutIdSchema = z.enum(INSPECTION_LAYOUT_IDS);
export type InspectionLayoutId = z.infer<typeof inspectionLayoutIdSchema>;

// A normalized coordinate (0..1) relative to the photo box.
const norm = () => z.number().min(0).max(1);

// Annotations render with a fixed stroke width (a render constant), but color,
// text size and arrow head size ARE adjustable per annotation. All three are
// optional and fall back to the shared render defaults (see
// shared/constants/inspection-annotations.ts), so annotations saved before a
// control existed keep rendering exactly as they did.
//
// Color is stored as a palette key rather than a hex, so the palette's actual
// colors stay tunable without rewriting saved documents.
const annoColor = () => z.enum(ANNO_COLOR_KEYS).optional();

// Tilt of a box-drawn shape (circle/rect), in degrees clockwise about the
// shape's own center. Measured in viewBox space — the space both renderers
// stretch — so the editor and the PDF apply the identical transform. Optional:
// an annotation saved before the rotate handle existed has none and renders
// exactly as it always did. Stored normalized to (-180, 180].
const annoRotation = () => z.number().optional();

export const arrowAnnotationSchema = z.object({
  id: z.string().uuid(),
  type: z.literal('arrow'),
  x1: norm(),
  y1: norm(),
  x2: norm(),
  y2: norm(),
  // Arrow head size in viewBox units (0..100 overlay space), i.e. the marker's
  // width/height. Optional; omitted arrows render at DEFAULT_ARROW_HEAD_SIZE.
  headSize: z.number().positive().optional(),
  color: annoColor(),
});

export const circleAnnotationSchema = z.object({
  id: z.string().uuid(),
  type: z.literal('circle'),
  x: norm(),
  y: norm(),
  rx: norm(),
  ry: norm(),
  rotation: annoRotation(),
  color: annoColor(),
});

// Outline rectangle — the circle's counterpart for square-edged callouts.
// Stored as a top-left corner plus extents (rather than the ellipse's center +
// radii) because that is exactly what a corner-to-corner drag produces, and what
// an <rect> takes. Outline only: like the circle it draws at the shared stroke
// width and carries no fill.
export const rectAnnotationSchema = z.object({
  id: z.string().uuid(),
  type: z.literal('rect'),
  x: norm(),
  y: norm(),
  w: norm(),
  h: norm(),
  rotation: annoRotation(),
  color: annoColor(),
});

export const textAnnotationSchema = z.object({
  id: z.string().uuid(),
  type: z.literal('text'),
  x: norm(),
  y: norm(),
  text: z.string(),
  // Font size in viewBox units (0..100 overlay space; matches the .ins-anno-text
  // default of 3.5). Optional; set on auto-lifted text to preserve its rendered
  // size, omitted on new page-level text which falls back to the render default.
  size: z.number().positive().optional(),
  color: annoColor(),
});

// A plain segment — the arrow's geometry with nothing drawn on the ends. With no
// head to size, its size control sets the stroke weight instead; the value is on
// the same scale as headSize, so one slider serves every segment type.
export const lineAnnotationSchema = z.object({
  id: z.string().uuid(),
  type: z.literal('line'),
  x1: norm(),
  y1: norm(),
  x2: norm(),
  y2: norm(),
  size: z.number().positive().optional(),
  color: annoColor(),
});

// Measurement line: an arrow head AND a perpendicular tick cap at each end.
// headSize sizes both, in the same viewBox units as the arrow's.
export const dimensionAnnotationSchema = z.object({
  id: z.string().uuid(),
  type: z.literal('dimension'),
  x1: norm(),
  y1: norm(),
  x2: norm(),
  y2: norm(),
  headSize: z.number().positive().optional(),
  color: annoColor(),
});

export const annotationSchema = z.discriminatedUnion('type', [
  arrowAnnotationSchema,
  lineAnnotationSchema,
  dimensionAnnotationSchema,
  circleAnnotationSchema,
  rectAnnotationSchema,
  textAnnotationSchema,
]);
export type Annotation = z.infer<typeof annotationSchema>;

// Non-destructive focal crop applied to a slot's photo + annotation layer
// together. zoom >= 1; offsets are normalized pan within the slot's clip box.
export const photoTransformSchema = z.object({
  zoom: z.number().min(1),
  offsetX: z.number(),
  offsetY: z.number(),
});
export type PhotoTransform = z.infer<typeof photoTransformSchema>;

export const photoSlotSchema = z.object({
  // The file this slot shows. Reads resolve through it.
  fileId: z.string().uuid().nullable().default(null),
  // Retained deliberately — NOT pending removal, whatever the file store's
  // cleanup epic did to `receipt_url`. Deleting this line is destructive:
  // `photoSlotSchema` is a plain `z.object()`, so Zod strips keys it does not
  // declare, and autosave revalidates the *whole* document through
  // `inspectionReportDocumentStrictSchema` on every save. Drop the line and
  // the next autosave silently strips `photoKey` out of the stored jsonb of
  // every report that still carries it. Not a rejection — a silent strip.
  //
  // It lives in jsonb rather than a column, so removing it properly would be
  // a data migration for no storage saved. It stays, and stays written.
  photoKey: z.string().nullable().optional(),
  caption: z.string().optional(),
  transform: photoTransformSchema,
  annotations: z.array(annotationSchema),
});
export type PhotoSlot = z.infer<typeof photoSlotSchema>;

// Constrained rich text: only heading/body paragraph styles and
// bold/italic/underline inline marks are representable. This is the serialized
// form produced and consumed by the editor's rich-text component.
export const richTextRunSchema = z.object({
  text: z.string(),
  bold: z.boolean().optional(),
  italic: z.boolean().optional(),
  underline: z.boolean().optional(),
});
export type RichTextRun = z.infer<typeof richTextRunSchema>;

export const richTextAlignSchema = z.enum(['left', 'center', 'right', 'justify']);
export type RichTextAlign = z.infer<typeof richTextAlignSchema>;

export const richTextBlockSchema = z.object({
  style: z.enum(['heading', 'body']),
  // Optional block-level formatting. Absent => layout defaults (left, base size).
  align: richTextAlignSchema.optional(),
  fontSize: z.number().min(8).max(72).optional(),
  runs: z.array(richTextRunSchema),
});
export type RichTextBlock = z.infer<typeof richTextBlockSchema>;

export const richTextSchema = z.array(richTextBlockSchema);
export type RichText = z.infer<typeof richTextSchema>;

// A page: a layout id plus its photo slots and text blocks, keyed by the
// slot/block keys the layout defines. Cross-checking those keys against the
// layout registry happens in the registry layer.
export const blockVAlignSchema = z.enum(['top', 'middle', 'bottom']);
export type BlockVAlign = z.infer<typeof blockVAlignSchema>;

// Region-level metadata keyed by block key. Vertical alignment positions the
// whole text region within its layout cell (like a table cell's valign), which
// is distinct from per-paragraph horizontal alignment. Optional + additive so
// existing documents stay valid.
export const blockMetaSchema = z.object({
  vAlign: blockVAlignSchema.optional(),
});

export const inspectionPageSchema = z.object({
  id: z.string().uuid(),
  layoutType: inspectionLayoutIdSchema,
  slots: z.record(z.string(), photoSlotSchema),
  blocks: z.record(z.string(), richTextSchema),
  blockMeta: z.record(z.string(), blockMetaSchema).optional(),
  // Free-floating annotations, coords normalized 0..1 of the whole page box.
  // Optional + additive so existing documents stay valid. See NEW-118.
  annotations: z.array(annotationSchema).optional(),
});
export type InspectionPage = z.infer<typeof inspectionPageSchema>;

export const inspectionReportHeaderSchema = z.object({
  address: z.string(),
  unit: z.string().optional(),
  title: z.string(),
  date: z.string(),
  issueSummary: richTextSchema.optional(),
  // Optional photo of the inspected building, shown on the cover sheet.
  // Resolved to a URL the same way slot photos are. Optional + nullable so
  // existing documents stay valid and the photo can be cleared.
  coverFileId: z.string().uuid().nullable().optional(),
  coverPhotoKey: z.string().nullable().optional(),
});
export type InspectionReportHeader = z.infer<typeof inspectionReportHeaderSchema>;

export const inspectionReportDocumentSchema = z.object({
  header: inspectionReportHeaderSchema,
  pages: z.array(inspectionPageSchema),
});
export type InspectionReportDocument = z.infer<typeof inspectionReportDocumentSchema>;

/**
 * `sent` is the working end state: a report is a draft until someone marks it
 * sent, and `sentAt` records when.
 *
 * `final` is kept because the column already holds it as a legal value and
 * nothing should start rejecting a row that has it — but nothing sets it, and
 * nothing ever did. It is not a step on the way to `sent`.
 */
export const inspectionReportStatusSchema = z.enum(['draft', 'final', 'sent']);
export type InspectionReportStatus = z.infer<typeof inspectionReportStatusSchema>;

// Create a new report under a job. The document is seeded server-side, so only
// an optional title is accepted.
export const createInspectionReportInputSchema = z.object({
  title: z.string().optional(),
});
export type CreateInspectionReportInput = z.infer<typeof createInspectionReportInputSchema>;
